import React, { useReducer, useMemo, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import { getUserDefinedFlags } from "@store/actions/userDefinedFlags.actions";
import { reducer, initialState } from "./state/reducer";
import { createActions } from "./state/actions";
import { selectKeyMetrics } from "./mappers/acwr.mapper";
import { monthKeyOf } from "./state/utils";
import { ST_SET_FILTER } from "./state/constants";
import { StatusTrackerDataContext, StatusTrackerModalContext } from "./context/contexts";

// Self-contained state for V2 — Context + useReducer, no global Redux slice. Team config is read
// from the existing Redux store read-only.
//
// `externalFilters` — a page that owns its own filter bar (the Overview) passes them here and
// this follows them. The standalone page passes nothing and its own FilterBar stays the only
// source, so its behaviour is unchanged.
// `onUnavailable` — called once the month view answers 403, so an embedder can render V1
// instead. Its presence also silences the gate warning, since the embedder handles it.
export const StatusTrackerV2Provider = ({ children, externalFilters, onUnavailable }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [selectedCellKey, setSelectedCellKey] = useState(null);
    const reduxDispatch = useDispatch();

    const teamSlice = useSelector(({ team }) => team);
    const udfLoaded = useSelector(({ userDefinedFlags }) => (userDefinedFlags?.flags?.length ?? 0) > 0);
    const { user } = useAuth0();
    const udfEnabled = (teamSlice?.info?.user_defined_flags_enabled ?? false) || isStaffEmail(user?.email);
    const team = useMemo(
        () => ({
            players: teamSlice?.players || [],
            positions: teamSlice?.positions || [],
            playerGroups: teamSlice?.playerGroups || [],
            info: teamSlice?.info || {},
            metrics: teamSlice?.metrics || [],
            keyMetrics: selectKeyMetrics(teamSlice?.metrics),
            flagNamesByType: teamSlice?.misc?.flag_names_by_type || {},
            teamName: teamSlice?.info?.display_name,
        }),
        [teamSlice]
    );

    const silentGate = !!onUnavailable;
    const actions = useMemo(() => createActions(dispatch, { silentGate }), [dispatch, silentGate]);
    const setFilter = useCallback((patch) => dispatch({ type: ST_SET_FILTER, payload: patch }), []);
    const openCell = useCallback((uniqueKey) => setSelectedCellKey(uniqueKey), []);
    const closeCell = useCallback(() => setSelectedCellKey(null), []);

    const { players, keyMetrics } = team;
    const { selectedMonth } = state.filters;

    // Follow the embedder's filters. Same key names as the V1 status tracker slice, and
    // ST_SET_FILTER merges, so filters V2 owns alone (flagTypesFilter) are left alone.
    useEffect(() => {
        if (externalFilters) setFilter(externalFilters);
    }, [externalFilters, setFilter]);

    // Tell the embedder the team is not on V2, so it can fall back.
    useEffect(() => {
        if (state.v2Enabled === false) onUnavailable?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.v2Enabled]);

    // Load user-defined flag configs once (for the flag-type filter options); read-only elsewhere.
    useEffect(() => {
        if (udfEnabled && !udfLoaded) reduxDispatch(getUserDefinedFlags());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [udfEnabled]);

    // One fetch per newly-visited month; cached months are 0-network (byMonth guard).
    useEffect(() => {
        if (!players.length || !selectedMonth) return;
        if (state.byMonth[monthKeyOf(selectedMonth)]) return;
        actions.fetchMonthGrid({ players, selectedMonth, keyMetrics });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth, players]);

    // Flag metadata drives only the info-button GPS checklist inside the cell modal — fetch it
    // lazily when a modal first opens, once per month (kept off the grid's critical path).
    useEffect(() => {
        if (!selectedCellKey || !selectedMonth) return;
        const mKey = monthKeyOf(selectedMonth);
        if (state.flagMetaMonths[mKey]) return;
        actions.fetchFlagMetadata(selectedMonth, mKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCellKey]);

    const dataValue = useMemo(
        () => ({ state, dispatch, actions, team, setFilter }),
        [state, actions, team, setFilter]
    );
    const modalValue = useMemo(
        () => ({ selectedCellKey, openCell, closeCell }),
        [selectedCellKey, openCell, closeCell]
    );

    return (
        <StatusTrackerDataContext.Provider value={dataValue}>
            <StatusTrackerModalContext.Provider value={modalValue}>
                {children}
            </StatusTrackerModalContext.Provider>
        </StatusTrackerDataContext.Provider>
    );
};
